/**
 * DynamicFilterDrawer — slide-out advanced filters panel.
 * Supports text, select, multiselect, date_range, number_range, status filter types.
 */
import { X, SlidersHorizontal } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetFooter,
} from '../ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import type { DynamicField, DynamicTableFilterValue } from '../../types/dynamicEntity';

interface DynamicFilterDrawerProps {
  columns: DynamicField[];
  filters: DynamicTableFilterValue[];
  onFiltersChange: (filters: DynamicTableFilterValue[]) => void;
}

function setFilterValue(
  draft: DynamicTableFilterValue[],
  fieldKey: string,
  value: unknown,
  valueTo?: unknown,
): DynamicTableFilterValue[] {
  const rest = draft.filter((f) => f.fieldKey !== fieldKey);
  if (value === '' || value == null || (Array.isArray(value) && value.length === 0)) return rest;
  return [...rest, { fieldKey, value, valueTo }];
}

function getFilter(
  draft: DynamicTableFilterValue[],
  fieldKey: string,
): DynamicTableFilterValue | undefined {
  return draft.find((f) => f.fieldKey === fieldKey);
}

export function DynamicFilterDrawer({
  columns,
  filters,
  onFiltersChange,
}: DynamicFilterDrawerProps) {
  const [draft, setDraft] = useState<DynamicTableFilterValue[]>(filters);
  const [open, setOpen] = useState(false);

  const filterableColumns = columns.filter((c) => c.displayConfig?.showInFilters);
  const activeCount = filters.length;

  const handleOpen = (v: boolean) => {
    if (v) setDraft([...filters]); // reset draft to current committed filters
    setOpen(v);
  };

  const handleApply = () => {
    onFiltersChange(draft);
    setOpen(false);
  };

  const handleClear = () => {
    setDraft([]);
  };

  return (
    <Sheet open={open} onOpenChange={handleOpen}>
      <SheetTrigger asChild>
        <Button variant="outline" size="sm" className="h-7 text-[11px] gap-1.5">
          <SlidersHorizontal className="w-3 h-3" />
          Filters
          {activeCount > 0 && (
            <span className="ml-0.5 bg-primary text-primary-foreground rounded-full px-1.5 text-[11px]">
              {activeCount}
            </span>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-80 p-0 flex flex-col">
        <SheetHeader className="px-4 py-3 border-b border-border">
          <SheetTitle className="text-[14px]">Advanced Filters</SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
          {filterableColumns.map((col) => {
            const filterType = col.displayConfig?.filterType ?? 'text';
            const current = getFilter(draft, col.internalName);

            return (
              <div key={col.fieldId} className="space-y-1.5">
                <Label className="text-[12px] font-medium">{col.label}</Label>

                {/* Text */}
                {filterType === 'text' && (
                  <Input
                    value={String(current?.value ?? '')}
                    onChange={(e) =>
                      setDraft(setFilterValue(draft, col.internalName, e.target.value || null))
                    }
                    placeholder={`Filter by ${col.label}…`}
                    className="h-7 text-[11px]"
                  />
                )}

                {/* Select / Status */}
                {(filterType === 'select' || filterType === 'status') && (
                  <Select
                    value={String(current?.value ?? '__all__')}
                    onValueChange={(v) =>
                      setDraft(setFilterValue(draft, col.internalName, v === '__all__' ? null : v || null))
                    }
                  >
                    <SelectTrigger className="h-7 text-[11px]">
                      <SelectValue placeholder={`Any ${col.label}`} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__" className="text-[11px]">Any</SelectItem>
                      {col.options?.map((o) => (
                        <SelectItem key={o.value} value={o.value} className="text-[11px]">
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                {/* Multiselect */}
                {filterType === 'multiselect' && (
                  <div className="space-y-1.5 border border-border rounded-md p-2 max-h-36 overflow-y-auto">
                    {col.options?.map((o) => {
                      const selected: string[] = Array.isArray(current?.value)
                        ? (current!.value as string[])
                        : [];
                      const checked = selected.includes(o.value);
                      return (
                        <div key={o.value} className="flex items-center gap-2">
                          <Checkbox
                            id={`${col.internalName}-${o.value}`}
                            checked={checked}
                            onCheckedChange={(c) => {
                              const next = c
                                ? [...selected, o.value]
                                : selected.filter((v) => v !== o.value);
                              setDraft(setFilterValue(draft, col.internalName, next.length ? next : null));
                            }}
                          />
                          <label
                            htmlFor={`${col.internalName}-${o.value}`}
                            className="text-[11px] cursor-pointer"
                          >
                            {o.label}
                          </label>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Date range */}
                {filterType === 'date_range' && (
                  <div className="flex items-center gap-2">
                    <Input
                      type="date"
                      value={String(current?.value ?? '')}
                      onChange={(e) =>
                        setDraft(
                          setFilterValue(
                            draft,
                            col.internalName,
                            e.target.value || null,
                            current?.valueTo,
                          ),
                        )
                      }
                      className="h-7 text-[11px]"
                    />
                    <span className="text-[11px] text-muted-foreground flex-shrink-0">to</span>
                    <Input
                      type="date"
                      value={String(current?.valueTo ?? '')}
                      onChange={(e) =>
                        setDraft(
                          setFilterValue(
                            draft,
                            col.internalName,
                            current?.value,
                            e.target.value || null,
                          ),
                        )
                      }
                      className="h-7 text-[11px]"
                    />
                  </div>
                )}

                {/* Number range */}
                {filterType === 'number_range' && (
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      placeholder="From"
                      value={String(current?.value ?? '')}
                      onChange={(e) =>
                        setDraft(
                          setFilterValue(
                            draft,
                            col.internalName,
                            e.target.value || null,
                            current?.valueTo,
                          ),
                        )
                      }
                      className="h-7 text-[11px]"
                    />
                    <span className="text-[11px] text-muted-foreground flex-shrink-0">–</span>
                    <Input
                      type="number"
                      placeholder="To"
                      value={String(current?.valueTo ?? '')}
                      onChange={(e) =>
                        setDraft(
                          setFilterValue(
                            draft,
                            col.internalName,
                            current?.value,
                            e.target.value || null,
                          ),
                        )
                      }
                      className="h-7 text-[11px]"
                    />
                  </div>
                )}

                {/* Boolean */}
                {filterType === 'boolean' && (
                  <Select
                    value={String(current?.value ?? '__all__')}
                    onValueChange={(v) =>
                      setDraft(setFilterValue(draft, col.internalName, v === '__all__' ? null : v || null))
                    }
                  >
                    <SelectTrigger className="h-7 text-[11px]">
                      <SelectValue placeholder="Any" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__" className="text-[11px]">Any</SelectItem>
                      <SelectItem value="true" className="text-[11px]">Yes</SelectItem>
                      <SelectItem value="false" className="text-[11px]">No</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
            );
          })}

          {filterableColumns.length === 0 && (
            <p className="text-[11px] text-muted-foreground text-center py-6">
              No filterable columns configured for this form.
            </p>
          )}
        </div>

        <SheetFooter className="flex items-center justify-between px-4 py-3 border-t border-border">
          <button
            type="button"
            onClick={handleClear}
            className="text-[11px] text-muted-foreground hover:text-destructive"
          >
            <X className="w-3 h-3 inline mr-1" />
            Clear all
          </button>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="h-7 text-[11px]" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button size="sm" className="h-7 text-[11px]" onClick={handleApply}>
              Apply
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
